import { Zap, Droplets, Train, RadioTower, Banknote, Landmark, HeartPulse, Server } from "lucide-react";
import { useLang } from "../context/LanguageContext";
import PageHero from "../components/PageHero";
import CtaBand from "../components/CtaBand";
import { Reveal, SectionHeading } from "../components/Reveal";

const HERO_IMG = "https://images.unsplash.com/photo-1614064548237-096f735f344f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTZ8MHwxfHNlYXJjaHwxfHxjeWJlcnNlY3VyaXR5JTIwbmV0d29ya3xlbnwwfHx8fDE3OTA2MDkxNTZ8MA&ixlib=rb-4.1.0&q=85";
const sectorIcons = [Zap, Droplets, Train, RadioTower, Banknote, Landmark, HeartPulse, Server];

export default function CriticalInfrastructure() {
  const { t } = useLang();
  const inf = t.infra;

  return (
    <>
      <PageHero
        testid="infra-hero"
        eyebrow={inf.eyebrow}
        titleLines={inf.title}
        paragraphs={[inf.p]}
        image={HERO_IMG}
      />

      <section className="py-20 lg:py-28" data-testid="infra-sectors">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow={inf.eyebrow} title={inf.sectorsTitle} />
          <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {inf.sectors.map((s, i) => {
              const Icon = sectorIcons[i];
              return (
                <Reveal key={s} delay={(i % 4) * 0.06}>
                  <div
                    className="group border border-sovereign/12 bg-obsidian/60 p-6 hover:border-sovereign/45 hover:bg-obsidian/80 transition-all duration-400"
                    data-testid={`infra-sector-${i}`}
                  >
                    <Icon className="w-6 h-6 text-sovereign" strokeWidth={1.5} aria-hidden="true" />
                    <p className="mt-4 font-mono text-[11px] tracking-[0.18em] uppercase text-slate-200">{s}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 border-t border-sovereign/10" data-testid="infra-lifecycle">
        <div className="max-w-7xl mx-auto px-6 lg:px-10">
          <SectionHeading eyebrow="NIS2" title={inf.stackTitle} />
          <Reveal delay={0.1}>
            <p className="mt-6 text-slate-400 max-w-2xl">{inf.stackText}</p>
          </Reveal>
          <div className="mt-14 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {inf.chain.map((step, i) => (
              <Reveal key={step} delay={i * 0.07}>
                <div
                  className="relative glass rounded-lg p-6 h-full group hover:border-sovereign/45 transition-colors duration-400"
                  data-testid={`infra-step-${i}`}
                >
                  <span className="font-mono text-2xl font-bold text-sovereign/25 group-hover:text-sovereign/50 transition-colors duration-400">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                  <p className="mt-4 font-display text-sm sm:text-base font-bold uppercase tracking-tight text-slate-100">
                    {step}
                  </p>
                  {i < inf.chain.length - 1 && (
                    <span className="hidden lg:block absolute top-1/2 -right-3 text-sovereign/50" aria-hidden="true">→</span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      <CtaBand title={inf.ctaTitle} text={inf.p} btn={inf.ctaBtn} />
    </>
  );
}
